import {PageModel} from '~~/package/mapper/common/model/PageModel';
import type {ProSkillManagerCandidateModel} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateModel';
import {ProSkillManagerCandidateStatusType} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateStatusType';
import {monitorCandidatesListData} from '@/components/ProSkillManager/main/content/sample/ProSkillMornitorSampleData';

function parseFilterParams(filter?: string): Record<string, string> {
    if (!filter || !filter.trim()) return {};
    return filter.split(',').reduce<Record<string, string>>((acc, part) => {
        const eq = part.indexOf('=');
        if (eq > 0) {
            acc[part.slice(0, eq).trim()] = part.slice(eq + 1).trim();
        }
        return acc;
    }, {});
}

function parseRange(rangeStr: string | undefined): [number, number] | null {
    if (rangeStr == null || typeof rangeStr !== 'string' || !rangeStr.trim()) return null;
    const parts = rangeStr.split('-').map(s => parseFloat(s.trim()));
    if (parts.length >= 2 && !Number.isNaN(parts[0]) && !Number.isNaN(parts[1])) {
        return [parts[0], parts[1]];
    }
    return null;
}

function valueInRange(value: number | null | undefined, range: [number, number] | null): boolean {
    if (range === null || value == null || Number.isNaN(value)) return true;
    const [min, max] = range;
    return value >= min && value < max + 1;
}

/**
 * 샘플 후보자 목록을 구간/상태/키워드로 필터링하고 페이지네이션하여 반환.
 * 서비스에서 assessmentId === 0 일 때만 사용.
 */
export function getFilteredSampleCandidatePage(
    filter: string | undefined,
    status: string[] | undefined,
    page: number,
    size: number
): PageModel<ProSkillManagerCandidateModel> {
    const params = parseFilterParams(filter);
    const onlyCompleted = status?.includes(ProSkillManagerCandidateStatusType.COMPLETED) ?? false;
    const list = monitorCandidatesListData.data as unknown as ProSkillManagerCandidateModel[];

    const filtered = list.filter((c) => {
        const cAny = c as unknown as {
            overallInfo?: { status?: string; currentOverallScore?: number; skillAcquisitionRate?: number; timeTaken?: number };
            trackResults?: Array<{ trackLevelId: number; currentTrackScore?: number; skillAcquisitionRate?: number; timeTaken?: number }>;
            name?: string;
            email?: string;
        };
        const overall = cAny.overallInfo;
        if (onlyCompleted && overall?.status !== 'COMPLETED') return false;

        const trackLevelIdParam = params.trackLevelId;
        const trackLevelId = trackLevelIdParam ? parseInt(trackLevelIdParam, 10) : null;
        const trackResults = cAny.trackResults;
        const track = trackLevelId != null && !Number.isNaN(trackLevelId) && trackResults
            ? trackResults.find((t) => t.trackLevelId === trackLevelId)
            : null;

        if (trackLevelId != null && !track) return false;

        const scoreRange = parseRange(params.score);
        const skillRange = parseRange(params.skillAcquisitionRate);
        const timeRange = parseRange(params.timeTaken);

        const useOverall = track == null;
        const score = useOverall ? overall?.currentOverallScore : track?.currentTrackScore;
        const skill = useOverall ? overall?.skillAcquisitionRate : track?.skillAcquisitionRate;
        /** 샘플/API의 timeTaken은 초 단위, 차트 필터 구간은 분 단위이므로 분으로 변환 후 비교 */
        const timeSec = useOverall ? overall?.timeTaken : track?.timeTaken;
        const timeMinutes = timeSec != null ? Math.floor(timeSec / 60) : undefined;

        if (scoreRange && !valueInRange(score, scoreRange)) return false;
        if (skillRange && !valueInRange(skill, skillRange)) return false;
        if (timeRange && !valueInRange(timeMinutes, timeRange)) return false;

        if (params.keyword) {
            const kw = params.keyword.toLowerCase();
            const name = (cAny.name ?? '').toLowerCase();
            const email = (cAny.email ?? '').toLowerCase();
            if (!name.includes(kw) && !email.includes(kw)) return false;
        }

        return true;
    });

    const total = filtered.length;
    const start = (page - 1) * size;
    const paged = filtered.slice(start, start + size);
    const last = start + paged.length >= total;

    return new PageModel<ProSkillManagerCandidateModel>(paged, last, page, total);
}

/**
 * 샘플 후보자 목록을 상태(status)만으로 필터링하여 전체 배열 반환.
 * 메인 대시보드 등에서 페이지네이션 없이 COMPLETED만 보여줄 때 사용.
 */
export function getFilteredSampleCandidates(status?: string[]): ProSkillManagerCandidateModel[] {
    const onlyCompleted = status?.includes(ProSkillManagerCandidateStatusType.COMPLETED) ?? false;
    const list = monitorCandidatesListData.data as unknown as ProSkillManagerCandidateModel[];

    if (!onlyCompleted) return list;

    return list.filter((c) => {
        const overall = (c as unknown as { overallInfo?: { status?: string } }).overallInfo;
        return overall?.status === 'COMPLETED';
    });
}
